import { decamelizeKeys } from 'humps';
import localStorage from 'local-storage';
import compact from 'lodash/compact';
import concat from 'lodash/concat';
import isString from 'lodash/isString';
import reduce from 'lodash/reduce';
import get from 'lodash/get';
import find from 'lodash/find';
import head from 'lodash/head';
import pickBy from 'lodash/pickBy';
import every from 'lodash/every';
import isEmpty from 'lodash/isEmpty';

import DefaultImage from './default.jpg';


const ACCESS_TOKEN_KEY = 'rewards:auth_token';
const LANGUAGE_KEY = 'allHubLang';
const ISANONYMOUS = 'inAsAnon';
export function formatName(object) {
  if (!object) {
    return '';
  }
  const values = compact(concat([],
    object.firstName || object.shippingFirstName,
    object.lastName || object.shippingLastName));
  return values.join(', ');
}

export function formatAddress(address) {
  if (!address) {
    return '';
  }
  const values = compact(concat([],
    address.street || address.shippingStreet,
    address.city || address.shippingCity,
    address.state || address.shippingState,
    address.zip || address.shippingZip));
  return values.join(', ');
}

export function setAccessToken(value) {
  const token = isString(value) ? {
    accessToken: value,
    refreshToken: value,
    expiresIn: 3600,
    tokenType: 'Bearer',
  } : value;
  localStorage(ACCESS_TOKEN_KEY, {
    ...token,
    expires: new Date().getTime() + (token.expiresIn * 1000),
  });
}

export function setUserType(value) {
  const userType = value !== 'false' ? 'isAnonymous' : 'notAnonymous';
  localStorage(ISANONYMOUS, userType);
}
export function getUserType() {
  return localStorage(ISANONYMOUS);
}
export function setLanguage(value) {
  localStorage(LANGUAGE_KEY, value);
}
export function getLanguage() {
  return localStorage(LANGUAGE_KEY);
}
export function removeLanguage() {
  localStorage.remove(LANGUAGE_KEY);
}
export function getAccessToken() {
  return localStorage(ACCESS_TOKEN_KEY);
}

export function isAccessTokenExpired(token) {
  return token && token.expires <= new Date().getTime();
}

export function toUrlParameters(object) {
  const params = decamelizeKeys(object);
  return reduce(params, (res, value, key) => {
    res.push(`${key}=${encodeURIComponent(value)}`);
    return res;
  }, []).join('&');
}

export function getDefaultImage(value) {
  // use default with fallback to first image
  const images = get(value, 'images') || value;
  return find(images, 'isDefault') || head(images) || {
    url: DefaultImage,
  };
}

export function getDefaultImageSrc(value) {
  const image = getDefaultImage(value);
  return get(image, 'url');
}

export function getRewardConfiguration(reward, fields) {
  const { template, additionalFields: { configurations } } = reward;
  if (!configurations) {
    return null;
  } else if (configurations.length === 1) {
    // return default config if only 1
    return head(configurations);
  }
  return find(configurations, (configuration) => {
    const values = pickBy(configuration.values, (value, key) => !!find(template.fields, { uid: key, editable: true }));
    // return config if empty and user didn't select any field
    if (!fields && every(values, isEmpty)) {
      return true;
    }
    // return config matching fields selected
    return fields ? reduce(fields, (res, field) => {
      const value = values[field.uid];
      return res && (field.value === value || !value);
    }, true) : null;
  });
}

export function getRatioValue(ratio, width) {
  const array = ratio.split(':');
  if (array.length !== 2) {
    return '100%';
  }
  if (width) {
    return width / (array[0] / array[1]);
  }
  return `${(parseInt(array[1], 10) / parseInt(array[0], 10)) * 100}%`;
}

export function getStylesRatio(ratio) {
  // https://www.w3schools.com/howto/howto_css_aspect_ratio.asp
  return {
    styleContainer: {
      position: 'relative',
      width: '100%',
      paddingTop: getRatioValue(ratio),
    },
    styleContent: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
    },
  };
}

export function getSpanishMonth(month) {
  const monthName = month.split(' ')[0];
  const date = month.split(' ')[1];
  const year = month.split(' ')[2];
  const months = {
    January: 'Enero',
    February: 'Febrero',
    March: 'Marzo',
    April: 'Abril',
    May: 'Mayo',
    June: 'Junio',
    July: 'Julio',
    August: 'Agosto',
    September: 'Septiembre',
    October: 'Octubre',
    November: 'Noviembre',
    December: 'Diciembre',
  };
  return `${months[monthName]} ${date} ${year}`;
}
